import classNames from "classnames";
import {
  extractZodDescription,
  isSpecialComponentDescriptions,
  renderSpecialMetaDescriptions,
} from "~/components/formElements/schemaToForm/renderSchemaBasedFormElement";
import {
  hasControlledFieldConfig,
  type ControlledFieldConfig,
} from "~/domains/pageSchemas";
import { type SchemaObject } from "~/domains/userData";
import { type StrapiFormComponent } from "~/services/cms/models/formElements/StrapiFormComponent";
import { type DynamicOptions } from "~/services/validation/dynamicSelect";
import { getNestedSchema } from "../formElements/schemaToForm/getNestedSchema";
import {
  getFieldSetByFieldName,
  renderFieldSet,
} from "../formElements/schemaToForm/renderFieldSet";
import {
  isZodEnum,
  renderZodEnum,
} from "../formElements/schemaToForm/renderZodEnum";
import {
  isZodObject,
  renderZodObject,
} from "../formElements/schemaToForm/renderZodObject";
import {
  isZodString,
  renderZodString,
} from "../formElements/schemaToForm/renderZodString";
import { sortSchemaByFormComponents } from "../formElements/schemaToForm/sortSchemaByFormComponents";

type Props = {
  pageConfig: {
    pageSchema?: SchemaObject;
    controlledFieldConfig?: ControlledFieldConfig;
  };
  formComponents?: StrapiFormComponent[];
  className?: string;
  readOnlyFieldNames: string[];
  dynamicOptions?: DynamicOptions;
};

export const SchemaComponents = ({
  pageConfig: { pageSchema, controlledFieldConfig },
  formComponents,
  className,
  readOnlyFieldNames,
  dynamicOptions,
}: Props) => {
  const sortedFieldsSchema = sortSchemaByFormComponents(
    pageSchema ?? {},
    formComponents,
  );

  if (Object.entries(sortedFieldsSchema).length === 0) return null;

  return (
    <div
      className={classNames("flex flex-col gap-kern-space-x-large", className)}
    >
      {Object.entries(sortedFieldsSchema).map(([fieldName, fieldSchema]) => {
        const fieldSetGroup = getFieldSetByFieldName(
          fieldName,
          formComponents ?? [],
        );
        const hasControlledField = hasControlledFieldConfig(
          fieldName,
          controlledFieldConfig,
        );

        if (fieldSetGroup !== undefined) {
          return renderFieldSet(
            fieldName,
            fieldSetGroup,
            readOnlyFieldNames,
            pageSchema,
          );
        }

        const matchingElement = formComponents
          ?.filter(
            (formComponents) =>
              formComponents.__component !== "form-elements.fieldset",
          )
          .find(({ name }) => name === fieldName);

        const description = extractZodDescription(fieldSchema);
        if (isSpecialComponentDescriptions(description)) {
          return renderSpecialMetaDescriptions(
            fieldName,
            description,
            fieldSchema,
            controlledFieldConfig,
            matchingElement,
            dynamicOptions,
          );
        }

        const nestedSchema = getNestedSchema(fieldSchema);
        const isFieldReadOnly = readOnlyFieldNames.includes(fieldName);

        if (isZodObject(nestedSchema)) {
          return renderZodObject(
            nestedSchema,
            fieldName,
            readOnlyFieldNames,
            formComponents,
            dynamicOptions,
          );
        }

        if (isZodEnum(nestedSchema))
          return renderZodEnum(nestedSchema, fieldName, matchingElement);

        if (isZodString(nestedSchema))
          return renderZodString(
            fieldName,
            isFieldReadOnly,
            matchingElement,
            hasControlledField,
          );
      })}
    </div>
  );
};
